import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './PaymentLinkDashboard.css';
import { workshopDomain } from '../../util/domain';

const PaymentLinkDashboard = () => {
    const [paymentLinks, setPaymentLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filters, setFilters] = useState({
        search: '',
        status: '',
        provider: '',
        page: 1,
        limit: 20,
        sort_by: 'created_at',
        sort_order: 'DESC'
    });
    const [pagination, setPagination] = useState({
        current_page: 1,
        total_pages: 1,
        total_records: 0,
        per_page: 20
    });


    useEffect(() => {
        fetchPaymentLinks();
    }, []);

    const fetchPaymentLinks = async () => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams(filters).toString();
            const response = await axios.get(`${workshopDomain}/api/paymentlink/admin/all?${queryParams}`);

            if (response.data.success) {
                setPaymentLinks(response.data.data.payment_links);
                const page = response.data.data.pagination;
                page.current_page = parseInt(page.current_page);
                page.total_pages = parseInt(page.total_pages);
                page.total_records = parseInt(page.total_records);
                page.per_page = parseInt(page.per_page);
                setPagination(page);
                setError('');
            } else {
                setError('Failed to fetch payment links');
            }
        } catch (error) {
            console.error('Fetch error:', error);
            setError(error.response?.data?.message || 'An error occurred while fetching payment links');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: 1 // Reset to first page when filters change
        }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchPaymentLinks();
    };

    const handlePageChange = (newPage) => {
        setFilters(prev => ({
            ...prev,
            page: newPage
        }));
    };

    const handleSort = (column) => {
        const newOrder = filters.sort_by === column && filters.sort_order === 'DESC' ? 'ASC' : 'DESC';
        setFilters(prev => ({
            ...prev,
            sort_by: column,
            sort_order: newOrder
        }));
    };

    const getStatusBadge = (status) => {
        const statusClasses = {
            'INITIATE': 'status-initiate',
            'PENDING': 'status-pending',
            'SUCCESS': 'status-success',
            'FAILED': 'status-failed'
        };
        return <span className={`status-badge ${statusClasses[status]}`}>{status}</span>;
    };

    const getSMSStatusBadge = (status) => {
        const statusClasses = {
            'accepted': 'sms-accepted',
            'sent': 'sms-sent',
            'delivered': 'sms-delivered',
            'read': 'sms-read',
            'failed': 'sms-failed'
        };
        return <span className={`sms-badge ${statusClasses[status]}`}>{status}</span>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString();
    };

    const formatAmount = (amount) => {
        return `₹${parseFloat(amount).toLocaleString()}`;
    };


    return (
        <div className="payment-link-dashboard">
            <div className="dashboard-header">
                <h1>Payments Dashboard</h1>
                <div className="dashboard-stats">
                    <div className="stat-card">
                        <span className="stat-label">Total Records</span>
                        <span className="stat-value">{pagination.total_records}</span>
                    </div>
                </div>
            </div>

            <div className="filters-section">
                <form onSubmit={handleSearch} className="search-form">
                    <div className="search-group">
                        <input
                            type="text"
                            placeholder="Search by mobile number or hall ticket ID..."
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            className="search-input"
                        />
                        <button type="submit" className="search-btn">Search</button>
                    </div>
                </form>

                <div className="filter-controls-row">
                    <div className="filter-controls">
                        <select
                            value={filters.status}
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                            className="filter-select"
                        >
                            <option value="">All Statuses</option>
                            <option value="INITIATE">Initiate</option>
                            <option value="PENDING">Pending</option>
                            <option value="SUCCESS">Success</option>
                            <option value="FAILED">Failed</option>
                        </select>

                        <select
                            value={filters.provider}
                            onChange={(e) => handleFilterChange('provider', e.target.value)}
                            className="filter-select"
                        >
                            <option value="">All Providers</option>
                            <option value="SMS">SMS</option>
                            <option value="WHATSAPP">WhatsApp</option>
                        </select>

                        <select
                            value={filters.limit}
                            onChange={(e) => handleFilterChange('limit', e.target.value)}
                            className="filter-select"
                        >
                            <option value="10">10 per page</option>
                            <option value="20">20 per page</option>
                            <option value="50">50 per page</option>
                        </select>
                    </div>

                </div>
            </div>

            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}

            <div className="table-container">
                {loading ? (
                    <div className="loading-spinner">Loading...</div>
                ) : (
                    <table className="payment-links-table">
                        <thead>
                            <tr>
                                <th onClick={() => handleSort('mobile_number')} className="sortable">
                                    Mobile Number
                                    {filters.sort_by === 'mobile_number' && (
                                        <span className="sort-indicator">
                                            {filters.sort_order === 'ASC' ? '↑' : '↓'}
                                        </span>
                                    )}
                                </th>
                                <th onClick={() => handleSort('hall_ticket_id')} className="sortable">
                                    Hall Ticket ID
                                    {filters.sort_by === 'hall_ticket_id' && (
                                        <span className="sort-indicator">
                                            {filters.sort_order === 'ASC' ? '↑' : '↓'}
                                        </span>
                                    )}
                                </th>
                                <th>Amount</th>
                                <th onClick={() => handleSort('payment_status')} className="sortable">
                                    Payment Status
                                    {filters.sort_by === 'payment_status' && (
                                        <span className="sort-indicator">
                                            {filters.sort_order === 'ASC' ? '↑' : '↓'}
                                        </span>
                                    )}
                                </th>
                                <th onClick={() => handleSort('provider')} className="sortable">
                                    Provider
                                    {filters.sort_by === 'provider' && (
                                        <span className="sort-indicator">
                                            {filters.sort_order === 'ASC' ? '↑' : '↓'}
                                        </span>
                                    )}
                                </th>
                                <th>SMS/WhatsApp Status</th>
                                <th onClick={() => handleSort('created_at')} className="sortable">
                                    Created At
                                    {filters.sort_by === 'created_at' && (
                                        <span className="sort-indicator">
                                            {filters.sort_order === 'ASC' ? '↑' : '↓'}
                                        </span>
                                    )}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {paymentLinks.length > 0 ? (
                                paymentLinks.map((link) => (
                                    <tr key={link.id}>
                                        <td>{link.mobile_number}</td>
                                        <td>{link.hall_ticket_id}</td>
                                        <td>{formatAmount(link.amount)}</td>
                                        <td>{getStatusBadge(link.payment_status)}</td>
                                        <td>
                                            <span className={`provider-badge ${link.provider.toLowerCase()}`}>
                                                {link.provider}
                                            </span>
                                        </td>
                                        <td>{getSMSStatusBadge(link.sms_status)}</td>
                                        <td>{formatDate(link.created_at)}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="no-data">
                                        No payment links found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {pagination.total_pages > 1 && (
                <div className="pagination">
                    <button
                        onClick={() => handlePageChange(pagination.current_page - 1)}
                        disabled={pagination.current_page === 1}
                        className="pagination-btn"
                    >
                        Previous
                    </button>

                    <span className="pagination-info">
                        Page {pagination.current_page} of {pagination.total_pages}
                    </span>

                    <button
                        onClick={() => handlePageChange(pagination.current_page + 1)}
                        disabled={pagination.current_page === pagination.total_pages}
                        className="pagination-btn"
                    >
                        Next
                    </button>
                </div>
            )}

        </div>
    );
};

export default PaymentLinkDashboard; 